import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TranslateModule } from '@ngx-translate/core';

import { SpDirectivesModule } from '@common/directives/directives.module';
import { SpPipesModule } from '@common/pipes/pipes.module';
import { SpPartnersCatalogComponent } from '@components/sp-partners-catalog';

import { SpLoaderModule } from '@ui/sp-loader';
import { ToolbarWidgetComponent } from '@ui/widget';
import { SpNpsComponent } from '@ui/sp-nps/nps-loader.component';

import { SpBaseGridLayoutComponent } from '@common/layouts/sp-base-grid-layout';
import { SpBaseLogoComponent } from '@common/layouts/sp-base-logo';
import { SpBaseHeaderComponent } from '@common/layouts/sp-base-header';

import { ModalsModule } from '@web/shared/modals/modals.module';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';
// import { ToolbarWidgetComponent as WebToolbarWidgetComponent } from '@web/shared/components/web-toolbar-widget/web-toolbar-widget.component';
// import { ToolbarStructureComponent } from '@web/shared/components/web-toolbar-structure/web-toolbar-structure.component';
import { WebColumnResizeComponent } from '@web/shared/components/web-column-resize/web-column-resize.component';
import { WebGridCellResizeComponent } from '@web/shared/components/web-grid-cell-resize/web-grid-cell-resize.component';
import { WebEditorModule } from '@web/shared/components/web-editor/web-editor.module';

import { MainComponent } from './main/main.component';
import { PreviewComponent } from './preview/preview.component';
import { HeaderComponent } from './header/header.component';
import { BlockDrawerComponent } from './drawers/block-drawer/block-drawer.component';

import { PageDrawerModule } from './drawers/page-drawer/page-drawer.module';
import { NavigationDrawerComponent } from './drawers/navigation-drawer/navigation-drawer.component';

import { AiChatDrawerComponent } from './drawers/ai-chat-drawer/ai-chat-drawer.component';

import {
    BlockShapeDividers,
    DefaultBlockComponent,
    SharedBlockComponent,
    SpecificBlockComponent,
    ZeroBlockComponent,
} from './content/page-content/components/blocks';

import {
    DefaultSectionComponent,
    SpecificSectionComponent,
    ZeroSectionComponent,
} from './content/page-content/components/sections';

import {
    DefaultColumnComponent,
    SpecificColumnComponent,
    ZeroColumnComponent,
} from './content/page-content/components/columns';

import { DefaultTabComponent } from './content/page-content/components/tabs';
import { DefaultGridComponent } from './content/page-content/components/grids';
import {
    DefaultWidgetComponent,
    SpecificWidgetComponent,
    GridWidgetComponent,
} from './content/page-content/components/widgets';

import {
    DefaultBlockControlsComponent,
    DefaultColumnControlsComponent,
    GridControlsComponent,
    ItemControlsComponent,
    SharedBlockControlsComponent,
    SpecificBlockControlsComponent,
    SpecificSectionControlsComponent,
    TabControlsComponent,
    ZeroBlockControlsComponent,
    ZeroWidgetControlsComponent,
} from './content/page-content/components/controls';

import { ContentComponent } from './content/content.component';
import { PageContentComponent } from './content/page-content/page-content.component';
import { DragHandleComponent } from './content/page-content/components/drag-handle/drag-handle.component';
import { AddButtonsComponent } from './content/page-content/components/add-buttons/add-buttons.component';
import { AddWidgetOrGridComponent } from './content/page-content/components/add-widget-or-grid';
import { ResizableAddButtonsComponent } from './content/page-content/components/resizable-add-buttons/resizable-add-buttons.component';
import { AddWidgetComponent } from './content/page-content/components/add-widget';
import { AddZeroWidgetComponent } from './content/page-content/components/add-zero-widget';
import { DisplayBlockComponent } from './content/page-content/components/display-block/display-block.component';
import { SettingsPanelComponent } from './settings-panel/settings-panel.component';

import { SingleHoverDirective } from './content/page-content/directives/single-hover.directive';
import { RotationHandlerDirective } from './content/page-content/directives/rotation-handler.directive';
import { HorizontalResizeHandlerDirective } from './content/page-content/directives/horizontal-resize-handler.directive';
import { VerticalResizeHandlerDirective } from './content/page-content/directives/vertical-resize-handler.directive';
import { ResizeCornerHandleDirective } from './content/page-content/directives/corner-resize-handler.directive';
import { BlockHeightResizeDirective } from './content/page-content/directives/block-height-resize.directive';

import { ViewResolverComponent } from './content/page-content/resolvers/view-resolver.component';
import { ShopCartIconViewComponent } from '@web/widgets/shop-cart-icon/components';
import { UpgradePlanPopoverComponent } from '@web/shared/popovers/upgrade-plan/upgrade-plan-popover.component';
import { TabHeaderWidthComponent } from './content/page-content/components/tabs/tab-header-width/tab-header-width.component';
import { TabHeaderWidthDirective } from './content/page-content/components/tabs/tab-header-width/tab-header-width.directive';

const COMPONENTS = [
    MainComponent,
    HeaderComponent,
    ContentComponent,
    PageContentComponent,
    PreviewComponent,
    DefaultBlockComponent,
    SharedBlockComponent,
    SpecificBlockComponent,
    ZeroBlockComponent,
    DefaultSectionComponent,
    SpecificSectionComponent,
    ZeroSectionComponent,
    DefaultColumnComponent,
    SpecificColumnComponent,
    ZeroColumnComponent,
    DefaultTabComponent,
    TabHeaderWidthComponent,
    DefaultGridComponent,
    DefaultWidgetComponent,
    SpecificWidgetComponent,
    GridWidgetComponent,
    ItemControlsComponent,
    DefaultBlockControlsComponent,
    DefaultColumnControlsComponent,
    GridControlsComponent,
    SharedBlockControlsComponent,
    SpecificBlockControlsComponent,
    ZeroBlockControlsComponent,
    SpecificSectionControlsComponent,
    TabControlsComponent,
    ZeroWidgetControlsComponent,
    DragHandleComponent,
    AddButtonsComponent,
    ResizableAddButtonsComponent,
    AddWidgetComponent,
    AddZeroWidgetComponent,
    SettingsPanelComponent,
    ViewResolverComponent,
    // BlockDrawerComponent,
    DisplayBlockComponent,
    BlockShapeDividers,
    AiChatDrawerComponent,
];

const DIRECTIVES = [
    SingleHoverDirective,
    TabHeaderWidthDirective,
    RotationHandlerDirective,
    HorizontalResizeHandlerDirective,
    VerticalResizeHandlerDirective,
    ResizeCornerHandleDirective,
    BlockHeightResizeDirective,
];

const MODULES = [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslateModule,
    DragDropModule,
    LetDirective,
    BsDropdownModule,
    ModalModule,
    TooltipModule,
    SpLoaderModule,
    SpBaseGridLayoutComponent,
    SpBaseLogoComponent,
    SpBaseHeaderComponent,
    SpDirectivesModule,
    SpPipesModule,
    SpPartnersCatalogComponent,
    ToolbarWidgetComponent,
    ModalsModule,
    WebSettingsModule,
    WebColumnResizeComponent,
    WebGridCellResizeComponent,
    PageDrawerModule,
    NavigationDrawerComponent,
    SpNpsComponent,
    WebEditorModule,
    ShopCartIconViewComponent,
    AddWidgetOrGridComponent,
    BlockDrawerComponent,
];

@NgModule({
    imports: [...MODULES, ...COMPONENTS, ...DIRECTIVES],
    exports: [MainComponent, HeaderComponent],
})
export class LayoutModule {}
